import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ExternalLink, Loader2, TrendingUp, MousePointerClick, ShoppingCart, DollarSign, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('affiliate_token');
    if (!token) { navigate('/affiliate/login'); return; }
    loadDashboard(token);
  }, [navigate]);

  const loadDashboard = async (token) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAffiliate(res.data.affiliate);
      setStats(res.data.stats);
      setReferrals(res.data.referrals || []);
    } catch (err) {
      localStorage.removeItem('affiliate_token');
      navigate('/affiliate/login');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Referral link copied!');
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    navigate('/affiliate/login');
  };

  const commissionLabel = (type) => {
    if (type === 'percentage_10') return '10% of Sale';
    if (type === 'flat_300') return '$300 per Booking';
    if (type === 'flat_500') return '$500 per Booking';
    return 'Pending Setup';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F8F6' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#C06C54' }} />
      </div>
    );
  }

  if (!affiliate) return null;

  const baseUrl = 'https://sisterdiamonds.com';
  const refLinks = [
    { label: 'Retreat Landing Page', path: '/retreats' },
    { label: 'Retreat Booking Page', path: '/retreat-booking' },
    { label: 'Just Be Retreat', path: '/just-be-retreat' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F8F6' }}>
      {/* Header */}
      <div className="py-8 px-4" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif" }}>
              Affiliate Dashboard
            </p>
            <h1 className="text-2xl font-bold" style={{ color: '#E8DCCA', fontFamily: "'Playfair Display', serif" }}>
              Welcome, {affiliate.full_name}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="text-xs px-3 py-1" style={{
              backgroundColor: affiliate.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
              color: affiliate.status === 'active' ? '#059669' : '#d97706'
            }}>
              {affiliate.status === 'active' ? 'Active' : 'Pending Approval'}
            </Badge>
            <button onClick={handleLogout} className="p-2 rounded-lg transition-all hover:bg-white/10" title="Logout">
              <LogOut className="h-4 w-4" style={{ color: '#E8DCCA' }} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: MousePointerClick, label: 'Total Clicks', value: stats?.total_clicks || 0, color: '#1A3C34' },
            { icon: ShoppingCart, label: 'Conversions', value: stats?.total_conversions || 0, color: '#C06C54' },
            { icon: TrendingUp, label: 'Conversion Rate', value: stats?.total_clicks ? `${((stats.total_conversions / stats.total_clicks) * 100).toFixed(1)}%` : '0%', color: '#1A3C34' },
            { icon: DollarSign, label: 'Total Earned', value: `$${(stats?.total_earned || 0).toLocaleString()}`, color: '#059669' },
          ].map((stat, i) => (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${stat.color}10` }}>
                  <stat.icon className="h-5 w-5" style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'rgba(26,60,52,0.5)', fontFamily: "'Manrope', sans-serif" }}>{stat.label}</p>
                  <p className="text-xl font-bold" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g,'-')}`}>{stat.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Commission Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Commission Structure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5" style={{ color: '#C06C54' }} />
              <span className="text-sm font-semibold" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }} data-testid="commission-type">
                {commissionLabel(affiliate.commission_type)}
              </span>
              {!affiliate.commission_type && (
                <span className="text-xs" style={{ color: 'rgba(26,60,52,0.5)' }}>(will be set by admin after approval)</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Referral Links */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Your Referral Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {affiliate.status !== 'active' ? (
              <p className="text-sm" style={{ color: 'rgba(26,60,52,0.5)', fontFamily: "'Manrope', sans-serif" }}>
                Your referral links will be available once your account is approved.
              </p>
            ) : (
              refLinks.map((link, i) => {
                const url = `${baseUrl}${link.path}?ref=${affiliate.affiliate_code}`;
                return (
                  <div key={i} className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(26,60,52,0.03)' }}>
                    <ExternalLink className="h-4 w-4 shrink-0" style={{ color: '#C06C54' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>{link.label}</p>
                      <p className="text-xs truncate" style={{ color: 'rgba(26,60,52,0.5)', fontFamily: "'Manrope', sans-serif" }}>{url}</p>
                    </div>
                    <button
                      onClick={() => copyLink(url)}
                      className="p-2 rounded-lg transition-all hover:bg-white shrink-0"
                      data-testid={`copy-link-${i}`}
                    >
                      <Copy className="h-4 w-4" style={{ color: '#1A3C34' }} />
                    </button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Recent Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <p className="text-sm text-center py-6" style={{ color: 'rgba(26,60,52,0.4)', fontFamily: "'Manrope', sans-serif" }}>
                No referrals yet. Share your links to start earning!
              </p>
            ) : (
              <div className="space-y-2">
                {referrals.map((ref, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'rgba(26,60,52,0.03)' }}>
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>{ref.page_visited || 'Retreat Page'}</p>
                      <p className="text-xs" style={{ color: 'rgba(26,60,52,0.5)' }}>{new Date(ref.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge style={{
                      backgroundColor: ref.converted ? 'rgba(16,185,129,0.15)' : 'rgba(26,60,52,0.08)',
                      color: ref.converted ? '#059669' : 'rgba(26,60,52,0.5)'
                    }}>
                      {ref.converted ? 'Converted' : 'Click'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;
